// The app coming back to the front: a phone unlocked, an installed app brought back from the background, a
// tab clicked again. Phones freeze a backgrounded app (its connection may have died without a word), so on
// the way back the app checks its connection, reloads what may have changed (badges, the account) and looks
// for a newer version, the way native messengers catch up the moment they are opened.

type ResumeListener = (awayMs: number) => void;

const listeners = new Set<ResumeListener>();
let hiddenAt = typeof document !== 'undefined' && document.visibilityState === 'hidden' ? Date.now() : 0;

/** Called with how long the app was away (0 when it never hid, e.g. a frozen page thawing in front). */
export function onResume(l: ResumeListener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

function back() {
  if (document.visibilityState !== 'visible') return;
  const away = hiddenAt ? Date.now() - hiddenAt : 0;
  hiddenAt = 0;
  listeners.forEach((l) => {
    try {
      l(away);
    } catch {
      /* one listener failing must not stop the others */
    }
  });
}

if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') hiddenAt ||= Date.now();
    else back();
  });
  // Back from the back/forward cache, or thawed by the browser (Chrome's page lifecycle).
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) back();
  });
  document.addEventListener('resume', back);
}

// Something that must not be cut by a reload (a call in progress). The chat store sets it once it exists.
let busy: () => boolean = () => false;
export function setReloadGuard(fn: () => boolean): void {
  busy = fn;
}
// A notification was just tapped: the app is showing what it pointed at, so a new version waits (a reload now would
// throw that away and start everything again, connection included).
let quietUntil = 0;
export function quietReloads(ms: number): void {
  quietUntil = Math.max(quietUntil, Date.now() + ms);
}
export const reloadBlocked = (): boolean => busy() || Date.now() < quietUntil;
